/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOrganizationUserSessionIssuerMcpServersRequest,
  ListOrganizationUserSessionIssuerMcpServersSecurity,
} from "../models/operations/listorganizationusersessionissuermcpservers.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationUserSessionIssuerMcpServersInfiniteQuery,
  buildOrganizationUserSessionIssuerMcpServersQuery,
  OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
  OrganizationUserSessionIssuerMcpServersPageParams,
  OrganizationUserSessionIssuerMcpServersQueryData,
  prefetchOrganizationUserSessionIssuerMcpServers,
  prefetchOrganizationUserSessionIssuerMcpServersInfinite,
  queryKeyOrganizationUserSessionIssuerMcpServers,
  queryKeyOrganizationUserSessionIssuerMcpServersInfinite,
} from "./organizationUserSessionIssuerMcpServers.core.js";
export {
  buildOrganizationUserSessionIssuerMcpServersInfiniteQuery,
  buildOrganizationUserSessionIssuerMcpServersQuery,
  type OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
  type OrganizationUserSessionIssuerMcpServersPageParams,
  type OrganizationUserSessionIssuerMcpServersQueryData,
  prefetchOrganizationUserSessionIssuerMcpServers,
  prefetchOrganizationUserSessionIssuerMcpServersInfinite,
  queryKeyOrganizationUserSessionIssuerMcpServers,
  queryKeyOrganizationUserSessionIssuerMcpServersInfinite,
};

export type OrganizationUserSessionIssuerMcpServersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listMcpServers organizationUserSessionIssuers
 *
 * @remarks
 * List the live MCP servers, toolsets, and meta MCP servers attached to a user_session_issuer in the caller's organization, organization-owned or project-specific. A toolset served through an MCP server is listed once, as that server. Requires org:read.
 */
export function useOrganizationUserSessionIssuerMcpServers(
  request: ListOrganizationUserSessionIssuerMcpServersRequest,
  security?: ListOrganizationUserSessionIssuerMcpServersSecurity | undefined,
  options?: QueryHookOptions<
    OrganizationUserSessionIssuerMcpServersQueryData,
    OrganizationUserSessionIssuerMcpServersQueryError
  >,
): UseQueryResult<
  OrganizationUserSessionIssuerMcpServersQueryData,
  OrganizationUserSessionIssuerMcpServersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationUserSessionIssuerMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listMcpServers organizationUserSessionIssuers
 *
 * @remarks
 * List the live MCP servers, toolsets, and meta MCP servers attached to a user_session_issuer in the caller's organization, organization-owned or project-specific. A toolset served through an MCP server is listed once, as that server. Requires org:read.
 */
export function useOrganizationUserSessionIssuerMcpServersSuspense(
  request: ListOrganizationUserSessionIssuerMcpServersRequest,
  security?: ListOrganizationUserSessionIssuerMcpServersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationUserSessionIssuerMcpServersQueryData,
    OrganizationUserSessionIssuerMcpServersQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationUserSessionIssuerMcpServersQueryData,
  OrganizationUserSessionIssuerMcpServersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationUserSessionIssuerMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listMcpServers organizationUserSessionIssuers
 *
 * @remarks
 * List the live MCP servers, toolsets, and meta MCP servers attached to a user_session_issuer in the caller's organization, organization-owned or project-specific. A toolset served through an MCP server is listed once, as that server. Requires org:read.
 */
export function useOrganizationUserSessionIssuerMcpServersInfinite(
  request: ListOrganizationUserSessionIssuerMcpServersRequest,
  security?: ListOrganizationUserSessionIssuerMcpServersSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
    OrganizationUserSessionIssuerMcpServersQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
    OrganizationUserSessionIssuerMcpServersPageParams
  >,
  OrganizationUserSessionIssuerMcpServersQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
    OrganizationUserSessionIssuerMcpServersQueryError,
    InfiniteData<
      OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
      OrganizationUserSessionIssuerMcpServersPageParams
    >,
    QueryKey,
    OrganizationUserSessionIssuerMcpServersPageParams
  >({
    ...buildOrganizationUserSessionIssuerMcpServersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listMcpServers organizationUserSessionIssuers
 *
 * @remarks
 * List the live MCP servers, toolsets, and meta MCP servers attached to a user_session_issuer in the caller's organization, organization-owned or project-specific. A toolset served through an MCP server is listed once, as that server. Requires org:read.
 */
export function useOrganizationUserSessionIssuerMcpServersInfiniteSuspense(
  request: ListOrganizationUserSessionIssuerMcpServersRequest,
  security?: ListOrganizationUserSessionIssuerMcpServersSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
    OrganizationUserSessionIssuerMcpServersQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
    OrganizationUserSessionIssuerMcpServersPageParams
  >,
  OrganizationUserSessionIssuerMcpServersQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
    OrganizationUserSessionIssuerMcpServersQueryError,
    InfiniteData<
      OrganizationUserSessionIssuerMcpServersInfiniteQueryData,
      OrganizationUserSessionIssuerMcpServersPageParams
    >,
    QueryKey,
    OrganizationUserSessionIssuerMcpServersPageParams
  >({
    ...buildOrganizationUserSessionIssuerMcpServersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setOrganizationUserSessionIssuerMcpServersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      userSessionIssuerId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: OrganizationUserSessionIssuerMcpServersQueryData,
): OrganizationUserSessionIssuerMcpServersQueryData | undefined {
  const key = queryKeyOrganizationUserSessionIssuerMcpServers(...queryKeyBase);

  return client.setQueryData<OrganizationUserSessionIssuerMcpServersQueryData>(
    key,
    data,
  );
}

export function invalidateOrganizationUserSessionIssuerMcpServers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      userSessionIssuerId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "listMcpServers",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationUserSessionIssuerMcpServers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "listMcpServers",
    ],
  });
}
